import type {
  AuditField,
  CaseDisposition,
  CodedFields,
  SubmittedCodeVerdict,
  SubmittedFieldVerdict,
  VerdictId,
} from '../types';
import { AUDIT_FIELDS, AUDIT_QUALIFIERS, sameSet } from './fields';

// Verdict model + persistence for the case-review adjudication (HUB-504, S4–S7).
// Ported from the prototype's per-field verdict object + localStorage draft store.
// Pure functions here (toggle semantics, per-field completeness, the submit gate)
// are unit-tested directly; the React compare screen holds the verdicts object in
// state, persists it here, and reads the progress gate from `computeProgress`.
//
// BLIND-SAFE: every helper works off the opaque A/B slots (CodedFields) — nothing
// keys on, derives, or leaks which slot is the agent vs the human coder.

/** One single-value field's verdict. `correctCodes` is populated only when the
 *  verdict is `neither` (the codebook picker's entries). */
export interface SingleFieldVerdict {
  verdict?: VerdictId;
  reason?: string;
  note?: string;
  correctCodes?: string[];
}

/** Per-code adjudication inside a multi-value field row. */
export interface MultiRowVerdict {
  /** shared rows default correct (unless explicitly unchecked → false); one-sided
   *  rows default flagged (correct only when explicitly set true). */
  correct?: boolean;
  reason?: string;
  note?: string;
}

/** One multi-value field's verdict: per-code row state + auditor-added codes. */
export interface MultiFieldVerdict {
  rows?: Record<string, MultiRowVerdict>;
  /** auditor-added correct codes the record supports but both results missed */
  added?: string[];
  note?: string;
}

/** A disagreeing qualifier's adjudication (which side is correct). */
export interface QualifierVerdict {
  correct?: 'on' | 'off';
}

/** The whole case's verdict object — keyed by field key and `q_<qualifier>`, the
 *  flat shape the prototype persists. */
export type CaseVerdicts = Record<
  string,
  SingleFieldVerdict | MultiFieldVerdict | QualifierVerdict | undefined
>;

// ---- Single-value field verdict semantics (ported from FieldCompare.toggle) ----

/** Toggle one slot's "correct" state for a SINGLE field, returning the next verdict.
 *  When A === B the toggles are additive (both can be correct → `both`); when they
 *  differ only one side can be correct (mutually exclusive). */
export function toggleSingleSlot(
  current: VerdictId | undefined,
  which: 'a' | 'b',
  matched: boolean,
): VerdictId | undefined {
  const aC = current === 'both' || current === 'a';
  const bC = current === 'both' || current === 'b';
  if (matched) {
    const next = which === 'a' ? !aC : !bC;
    // Additive: with equal values, marking either slot correct means "both correct".
    return next ? 'both' : undefined;
  }
  if (which === 'a') return aC ? undefined : 'a';
  return bC ? undefined : 'b';
}

/** Toggle the "Neither is correct" verdict for a single field. */
export function toggleNeither(current: VerdictId | undefined): VerdictId | undefined {
  return current === 'neither' ? undefined : 'neither';
}

/** Whether a single field's reason is optional (an exact match confirmed as `both`
 *  needs no rationale). Any discrepancy verdict wants a reason. */
export function reasonOptional(state: SingleFieldVerdict | undefined, matched: boolean): boolean {
  return !!state && state.verdict === 'both' && matched;
}

/** Whether a single field has been resolved enough to auto-collapse (verdict picked
 *  AND a reason given, or a reason-optional match). */
export function singleFieldResolved(state: SingleFieldVerdict | undefined, matched: boolean): boolean {
  if (!state || !state.verdict) return false;
  return !!state.reason || reasonOptional(state, matched);
}

/** The submit gate for a single field: a verdict has been chosen (matches the
 *  prototype's `fieldDone` — reason is encouraged via collapse but not gated). */
export function singleFieldDone(state: SingleFieldVerdict | undefined): boolean {
  return !!(state && state.verdict);
}

// ---- Multi-value field verdict semantics (ported from MultiFieldCompare) ----

/** One row in a multi-field union: a distinct code and which slots carry it. */
export interface MultiUnionRow {
  code: string;
  a: string | null;
  b: string | null;
  shared: boolean;
}

/** Ordered union of a multi-field's A/B codes (A-first, then B-only). */
export function multiUnionRows(a: string[] | undefined, b: string[] | undefined): MultiUnionRow[] {
  const aCodes = a ?? [];
  const bCodes = b ?? [];
  const aset = new Set(aCodes);
  const bset = new Set(bCodes);
  const rows: MultiUnionRow[] = [];
  for (const c of aCodes) rows.push({ code: c, a: c, b: bset.has(c) ? c : null, shared: bset.has(c) });
  for (const c of bCodes) if (!aset.has(c)) rows.push({ code: c, a: null, b: c, shared: false });
  return rows;
}

/** Whether a union row is currently marked correct. Shared rows default correct;
 *  one-sided rows default flagged. */
export function multiRowCorrect(row: MultiUnionRow, rowState: MultiRowVerdict | undefined): boolean {
  const rs = rowState ?? {};
  return row.shared ? rs.correct !== false : rs.correct === true;
}

/** Whether a multi field is complete: no codes at all, or every flagged (wrong /
 *  one-sided) code carries a reason. Auditor-added codes never block completeness
 *  (mirrors the prototype). */
export function multiFieldComplete(a: string[], b: string[], state: MultiFieldVerdict | undefined): boolean {
  const rows = multiUnionRows(a, b);
  const added = state?.added ?? [];
  if (rows.length === 0 && added.length === 0) return true;
  const rowsState = state?.rows ?? {};
  const flagged = rows.filter((r) => !multiRowCorrect(r, rowsState[r.code]));
  return flagged.every((r) => !!rowsState[r.code]?.reason);
}

// ---- Qualifier verdict semantics ----

/** A qualifier is resolved when the two slots agree (nothing to adjudicate) or, when
 *  they disagree, the auditor has picked which side is correct. */
export function qualifierDone(
  a: CodedFields,
  b: CodedFields,
  key: 'emergency' | 'unusual_position',
  state: QualifierVerdict | undefined,
): boolean {
  if (a[key] === b[key]) return true;
  return !!(state && state.correct);
}

// ---- Whole-case progress + submit gate ----

export interface CaseProgress {
  done: number;
  total: number;
  /** true when every field + every disagreeing qualifier is resolved */
  complete: boolean;
}

/** Compute the case's review progress + the submit gate purely from the verdicts
 *  object. `done === total` enables Submit & next. Total = the compared fields
 *  plus the two qualifiers. */
export function computeProgress(a: CodedFields, b: CodedFields, verdicts: CaseVerdicts): CaseProgress {
  let done = 0;
  // Optional fields are counted in NEITHER done nor total, the same treatment
  // the qualifiers get below and for the same reason: a field that cannot hold
  // up the submit button must not sit in the denominator either. Counting them
  // in total alone would strand every case one short of complete; counting them
  // in both would report progress the reviewer never made.
  const required = AUDIT_FIELDS.filter((f) => !f.optional);
  for (const f of required) {
    const st = verdicts[f.key];
    if (f.multi) {
      if (multiFieldComplete(a[f.key] as string[], b[f.key] as string[], st as MultiFieldVerdict)) done += 1;
    } else if (singleFieldDone(st as SingleFieldVerdict)) {
      done += 1;
    }
  }
  // Qualifiers are NOT counted.
  //
  // Neither leg populates them. The agent does not emit them
  // ("left false until the coder (B) side defines the comparison") and FA
  // supplies nothing for them either, so both slots are hard-coded false and
  // the pair can never disagree — see parseAgentResult and the coder mapping in
  // evalmaterializer/apply.go. qualifierDone therefore returns true on every
  // case, and counting them opened every untouched case at 2/8, which reads as
  // "a quarter reviewed" when nothing has been.
  //
  // They still GATE submit, but only when they actually disagree. Today that is
  // unreachable; the day one side starts emitting them, an unadjudicated
  // disagreement blocks submission again on its own rather than slipping
  // through because a count was trimmed months earlier.
  const qualifiersResolved = AUDIT_QUALIFIERS.every((q) =>
    qualifierDone(a, b, q.key, verdicts['q_' + q.key] as QualifierVerdict),
  );
  const total = required.length;
  return { done, total, complete: done === total && qualifiersResolved };
}

/** Whether two slots of a field agree (symmetric; reveals no provenance). */
export function fieldMatched(field: AuditField, a: CodedFields, b: CodedFields): boolean {
  const va = a[field.key];
  const vb = b[field.key];
  return field.multi ? sameSet(va as string[], vb as string[]) : va === vb;
}

// ---- Draft verdicts → submission payload ----------------------------------
//
// The reviewer's draft is shaped for the SCREEN: single fields hold a verdict,
// multi fields hold per-code rows, qualifiers hold which side is right. The
// wire wants one verdict per field. Mapping between them is the whole of this
// section, and it exists because the previous mapping did not do it: it cast
// every entry to SingleFieldVerdict and read `.verdict`, so the three
// multi-value fields (which have no `.verdict`) and both qualifiers (which are
// not keyed by field at all) were dropped in silence. A reviewer resolved nine
// of nine and four verdicts reached the server.

/** The qualifier draft keys, as stored in CaseVerdicts (`q_<qualifier>`). */
export const QUALIFIER_PREFIX = 'q_';

/**
 * Derive a multi-value field's field-level verdict from its per-code rows.
 *
 * The question the study asks is "whose LIST was right", so a side wins only
 * when the accepted set is exactly its set. Anything else — a mixture drawn
 * from both, an empty acceptance, or a code the reviewer had to add — is
 * `neither`, which is the honest reading: no side's list, as submitted, was
 * the correct one.
 */
export function multiFieldVerdict(
  a: string[],
  b: string[],
  state: MultiFieldVerdict | undefined,
): VerdictId {
  const rows = multiUnionRows(a, b);
  const rowsState = state?.rows ?? {};
  const accepted = rows.filter((r) => multiRowCorrect(r, rowsState[r.code])).map((r) => r.code);
  // A code the reviewer added means both lists were incomplete, whatever the
  // rows say — so it can only be "neither", never a win for one side.
  if ((state?.added ?? []).length > 0) return 'neither';
  const matchesA = sameSet(accepted, a);
  const matchesB = sameSet(accepted, b);
  if (matchesA && matchesB) return 'both';
  if (matchesA) return 'a';
  if (matchesB) return 'b';
  return 'neither';
}

/** The per-code rows of a multi field, in union order then added codes. */
function multiCodeVerdicts(
  a: string[],
  b: string[],
  state: MultiFieldVerdict | undefined,
): SubmittedCodeVerdict[] {
  const rowsState = state?.rows ?? {};
  const out: SubmittedCodeVerdict[] = [];
  for (const r of multiUnionRows(a, b)) {
    const rs = rowsState[r.code] ?? {};
    out.push({
      code: r.code,
      correct: multiRowCorrect(r, rs),
      ...(rs.reason ? { reason: rs.reason } : {}),
      ...(rs.note ? { note: rs.note } : {}),
    });
  }
  for (const code of state?.added ?? []) {
    const rs = rowsState[code] ?? {};
    // An added code is by definition one the reviewer endorses — they typed it
    // in as what should have been coded.
    out.push({
      code,
      correct: true,
      added: true,
      ...(rs.reason ? { reason: rs.reason } : {}),
      ...(rs.note ? { note: rs.note } : {}),
    });
  }
  return out;
}

/**
 * The reviewer's chosen correct code(s) for a SINGLE-value field, as code rows.
 *
 * Only meaningful on `neither` — the picker that fills `correctCodes` is the one
 * revealed by "Neither is correct", and a stale draft could otherwise carry
 * codes under a verdict that names a side, which would claim the reviewer
 * endorsed a code they had since moved away from.
 *
 * `added` is computed, not assumed: a reviewer who rejects both results and then
 * picks a code one of the slots happened to carry has not added anything, and
 * saying they did would misreport where the code came from.
 */
function singleCorrectCodes(
  field: AuditField,
  a: CodedFields,
  b: CodedFields,
  state: SingleFieldVerdict,
): SubmittedCodeVerdict[] {
  if (state.verdict !== 'neither') return [];
  const slotValues = [a[field.key], b[field.key]];
  return (state.correctCodes ?? [])
    .filter((code) => !!code)
    .map((code) => ({ code, correct: true, added: !slotValues.includes(code) }));
}

/**
 * Map a qualifier's adjudication onto the A/B slot vocabulary the rest of the
 * payload uses: the reviewer says the correct value is on/off, and the slot
 * carrying that value is the one they endorsed.
 *
 * Returns undefined when the two slots AGREE. That is not the same as "both
 * correct": an agreeing qualifier is auto-counted as resolved without the
 * reviewer touching it (there is no control to touch), so submitting `both`
 * would record a judgement nobody made and fold it into the study's totals.
 */
export function qualifierVerdict(
  a: CodedFields,
  b: CodedFields,
  key: 'emergency' | 'unusual_position',
  state: QualifierVerdict | undefined,
): VerdictId | undefined {
  if (a[key] === b[key]) return undefined;
  if (!state?.correct) return undefined;
  const want = state.correct === 'on';
  return a[key] === want ? 'a' : 'b';
}

/**
 * Build the submission's per-field verdict map from the reviewer's draft.
 *
 * Keyed by field key, plus `emergency` / `unusual_position` for the qualifiers
 * — the plain names, not the `q_` draft keys, so the server's per-field metrics
 * read in the same vocabulary as every other field.
 *
 * A field the reviewer did not judge is OMITTED rather than sent as an empty
 * verdict: an unjudged field must not enter the denominator as if it had been.
 */
export function buildFieldVerdicts(
  a: CodedFields,
  b: CodedFields,
  verdicts: CaseVerdicts,
): Record<string, SubmittedFieldVerdict> {
  const out: Record<string, SubmittedFieldVerdict> = {};

  for (const f of AUDIT_FIELDS) {
    if (f.multi) {
      const st = verdicts[f.key] as MultiFieldVerdict | undefined;
      const aCodes = (a[f.key] as string[]) ?? [];
      const bCodes = (b[f.key] as string[]) ?? [];
      const codes = multiCodeVerdicts(aCodes, bCodes, st);
      // Nothing coded on either side and nothing added — there was nothing to
      // judge, so there is no judgement to report.
      if (codes.length === 0) continue;
      const entry: SubmittedFieldVerdict = {
        verdict: multiFieldVerdict(aCodes, bCodes, st),
        // Field-level reasons are the per-code ones, deduplicated in row order:
        // "why was this field wrong" is answered by the codes that were wrong.
        reasons: dedupe(codes.filter((c) => !c.correct || c.added).map((c) => c.reason)),
        codes,
      };
      if (st?.note) entry.note = st.note;
      out[f.key] = entry;
      continue;
    }

    const st = verdicts[f.key] as SingleFieldVerdict | undefined;
    if (!st?.verdict) continue;
    const entry: SubmittedFieldVerdict = {
      verdict: st.verdict,
      reasons: st.reason ? [st.reason] : [],
    };
    if (st.note) entry.note = st.note;
    // The reviewer's replacement code on a "neither" — collected by
    // CorrectCodeEntry, and until now dropped here, so it lived only in this
    // browser's localStorage. Without it the server sees a rejection of both
    // sides with no answer attached, which is unresolvable by construction: it
    // cannot enter the selection denominator, and the selection rates stay an
    // upper bound no matter how much reviewing gets done.
    const correct = singleCorrectCodes(f, a, b, st);
    if (correct.length > 0) entry.codes = correct;
    out[f.key] = entry;
  }

  for (const q of AUDIT_QUALIFIERS) {
    const st = verdicts[QUALIFIER_PREFIX + q.key] as QualifierVerdict | undefined;
    const verdict = qualifierVerdict(a, b, q.key, st);
    if (!verdict) continue;
    out[q.key] = { verdict, reasons: [] };
  }

  return out;
}

/** Non-empty, de-duplicated, order-preserving. */
function dedupe(values: (string | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

// ---- localStorage draft store (demo persistence; a live mutation lands later) ----

const V_PREFIX = 'audit_v_';
const GN_PREFIX = 'audit_gn_';
const DISPOSITION_KEY = 'audit_dispositions';

export function loadVerdicts(caseId: string): CaseVerdicts {
  try {
    const raw = localStorage.getItem(V_PREFIX + caseId);
    return raw ? (JSON.parse(raw) as CaseVerdicts) : {};
  } catch {
    return {};
  }
}

export function saveVerdicts(caseId: string, verdicts: CaseVerdicts): void {
  try {
    localStorage.setItem(V_PREFIX + caseId, JSON.stringify(verdicts));
  } catch {
    /* localStorage unavailable (private mode / tests) — non-fatal */
  }
}

export function loadGeneralNote(caseId: string): string {
  try {
    return localStorage.getItem(GN_PREFIX + caseId) ?? '';
  } catch {
    return '';
  }
}

export function saveGeneralNote(caseId: string, note: string): void {
  try {
    localStorage.setItem(GN_PREFIX + caseId, note);
  } catch {
    /* non-fatal */
  }
}

// Per-case disposition store (how the evaluator left each case): submitted, draft,
// or a routing flag. Session-local (preview) until the live submit slice persists
// server-side. Replaces the old submitted-ids set; submitted is just one value.
export function loadDispositions(): Record<string, CaseDisposition> {
  try {
    const raw = localStorage.getItem(DISPOSITION_KEY);
    return raw ? (JSON.parse(raw) as Record<string, CaseDisposition>) : {};
  } catch {
    return {};
  }
}

export function getDisposition(caseId: string): CaseDisposition | undefined {
  return loadDispositions()[caseId];
}

export function setDisposition(caseId: string, disposition: CaseDisposition): void {
  try {
    const all = loadDispositions();
    all[caseId] = disposition;
    localStorage.setItem(DISPOSITION_KEY, JSON.stringify(all));
  } catch {
    /* non-fatal */
  }
}

/** The set of cases resolved this session (any disposition removes a case from the
 *  "still to review" flow — a draft is set-aside, a flag routes it elsewhere). */
export function loadSubmittedIds(): Set<string> {
  return new Set(Object.keys(loadDispositions()));
}

export function markSubmitted(caseId: string): void {
  setDisposition(caseId, 'submitted');
}

export function isSubmitted(caseId: string): boolean {
  return getDisposition(caseId) === 'submitted';
}
